/** The pack's route: `/c/:cohortId/results/:roundNo/pack`, bare. A screen-only toolbar prints it; facilitators see "Every bank" once the api can serve every team's report. */
import type { Principal } from "@bsim/contracts";
import { ROUTES } from "@bsim/contracts";
import { Link } from "../../shell/history";
import { GxEmptyState, GxErrorBanner, GxWaiting } from "../../theme/primitives";
import { fiscalYearLabel } from "../../format";
import { useRoute } from "../../api/context";
import { useResultsData } from "../data";
import { Pack } from "./Pack";
import "./pack.css";

/** The facilitator's "every bank" needs one report per team in the cohort; the route is not in the contract yet, so the button stays dark until it is. */
export const FACILITATOR_PACK_ROUTE = "facilitatorRoundReports";
const facilitatorPackReady = (principal: Principal, cohortId: string): boolean =>
  Object.hasOwn(ROUTES, FACILITATOR_PACK_ROUTE) && principal.memberships.some((m) => m.cohortId === cohortId && m.role === "facilitator");

/** The pack is a bare page: its own main landmark and one level-one heading (said to a screen reader, not printed twice). */
export function PackPage(props: { readonly cohortId: string; readonly roundNo: number; readonly principal: Principal }) {
  return <main className="gx-pack-main"><h1 className="gx-sr">Board pack</h1><PackBody {...props} /></main>;
}

function PackBody({ cohortId, roundNo, principal }: { readonly cohortId: string; readonly roundNo: number; readonly principal: Principal }) {
  const data = useResultsData(cohortId, roundNo);
  const welcome = useRoute("welcomeCard", { cohortId }, { staleTime: Infinity });
  const back = `/c/${cohortId}/results/${roundNo}`;
  const toolbar = (
    <div className="gx-pagehead gx-pack-toolbar gx-noprint" data-testid="pack.toolbar">
      <Link href={back} className="gx-btn gx-btn--ghost">← Back to your results</Link>
      <div className="gx-pagehead-acts">
        {facilitatorPackReady(principal, cohortId) ? <Link href={`${back}/pack?banks=all`} className="gx-btn gx-btn--secondary">Every bank</Link> : null}
        <button type="button" className="gx-btn gx-btn--primary" onClick={() => window.print()}>Print / Save as PDF</button>
      </div>
    </div>
  );
  if (data.status === "error") return <GxErrorBanner error={data.error} />;
  if (data.status === "no-team") return <GxErrorBanner message="Your facilitator hasn't placed you on a team yet." />;
  if (data.status === "loading") return <GxWaiting what="the board pack" testId="pack.skeleton" height={400} />;
  if (data.status === "preparing") return <GxEmptyState title={`Results for ${fiscalYearLabel(data.catalog, roundNo)} are being prepared.`}>{data.team.cohort.mode === "solo" ? "The pack opens as soon as the year has run." : "The pack opens as soon as your facilitator releases the round."}</GxEmptyState>;
  return (
    <>
      {toolbar}
      <Pack team={data.team} catalog={data.catalog} market={data.market} report={data.report} previous={data.previous} draft={data.draft} bankName={welcome.data?.bankName} />
    </>
  );
}
